/**
 * certificateCatalogs — the server-supplied reference data for certificates.
 *
 * `views/users/users-certificates.twig` injects four catalogs alongside the
 * user's own certificates: types, grades, ratings and rating types. They
 * arrive as id-keyed maps in some deployments and as lists in others, so both
 * shapes are normalised here rather than in every component.
 *
 * These are the ONLY parts of the certificate form that need the server. The
 * per-type field and label maps are frontend constants — see
 * `certificateFields.js`.
 */
import { serverData } from './serverBootstrap.js';

function toList(raw) {
	if (Array.isArray(raw)) return raw.filter(Boolean);
	if (raw && typeof raw === 'object') {
		return Object.keys(raw)
			.map(k => {
				const v = raw[k];
				if (v == null) return null;
				return typeof v === 'object' ? { id: v.id != null ? v.id : k, ...v } : { id: k, name: v };
			})
			.filter(Boolean);
	}
	return [];
}

const str = v => (v == null ? '' : String(v));

/** Certificate types as `{ id, name }`, alphabetical. */
export function readTypes(src) {
	return toList((src || serverData()).certificateTypes)
		.map(t => ({ ...t, id: str(t.id), name: t.name || str(t.id) }))
		.sort((a, b) => a.name.localeCompare(b.name));
}

/** Grades as `{ id, name, certificateTypeID }`. */
export function readGrades(src) {
	return toList((src || serverData()).certificatesGrades).map(g => ({
		...g,
		id: str(g.id),
		name: g.name || str(g.id),
		certificateTypeID: str(g.certificateTypeID)
	}));
}

/** Ratings as `{ id, name, certificateTypeID, certificateRatingType }`. */
export function readRatings(src) {
	return toList((src || serverData()).ratings).map(r => ({
		...r,
		id: str(r.id),
		name: r.name || str(r.id),
		certificateTypeID: str(r.certificateTypeID),
		certificateRatingType: str(r.certificateRatingType)
	}));
}

/** Rating type groups as `{ id, name }` — the headings ratings are listed under. */
export function readRatingTypes(src) {
	return toList((src || serverData()).ratingsTypes).map(t => ({
		...t,
		id: str(t.id),
		name: t.name || str(t.id)
	}));
}

/** The user's certificates, normalised to a list. */
export function readCertificates(src) {
	return toList((src || serverData()).certificates);
}

/** Grades offered for one certificate type. */
export function gradesForType(grades, typeId) {
	const t = str(typeId);
	return grades.filter(g => g.certificateTypeID === t);
}

/** Ratings offered for one certificate type, excluding its instrument rating. */
export function ratingsForType(ratings, typeId, instrumentalId) {
	const t = str(typeId);
	const skip = str(instrumentalId);
	return ratings.filter(r => r.certificateTypeID === t && r.certificateRatingType !== skip);
}
